import { Slot } from "@radix-ui/react-slot";
import { type VariantProps, cva } from "class-variance-authority";
import * as React from "react";
import { cn } from "lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 w-full min-h-10 h-auto break-words text-center whitespace-normal sm:whitespace-nowrap font-semibold rounded-[50px] shadow-elevation-sm text-sm transition-all focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 hover:shadow-md disabled:shadow-sm disabled:hover:shadow-sm [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 disabled:cursor-not-allowed",
  {
    variants: {
      variant: {
        default:
          "bg-foundation-base-darkbase-dark-500 hover:bg-foundation-base-darkbase-dark-700 text-white disabled:bg-foundation-calm-blue-300 disabled:text-white disabled:hover:bg-foundation-calm-blue-100",
        secondary:
          "bg-foundation-calm-blue-50 border border-foundation-calm-blue-200 text-foundation-base-darkbase-dark-500 hover:bg-foundation-calm-blue-100 disabled:opacity-40 disabled:cursor-not-allowed",
        outline:
          "bg-transparent border border-foundation-base-darkbase-dark-500 text-foundation-base-darkbase-dark-500 hover:bg-foundation-calm-blue-50 disabled:opacity-40 disabled:cursor-not-allowed",
        ghost: "bg-transparent shadow-none hover:shadow-none hover:text-accent-foreground",
        },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  size?: "default" | "auto" | 'icon';
}

const   Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size = "default", asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    const sizeClasses = {
      default: "w-full px-5 py-3",
      auto: "w-auto flex-[0_0_auto] px-5 py-3",
      icon: "h-9 w-9 min-h-0 p-0",
    };
    
    return (
      <Comp
        className={cn(
          buttonVariants({ variant }),
          sizeClasses[size],
          className
        )}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
